import { getCoreGeolocationGeocountries } from 'data-api'
import groupBy from 'lodash/groupBy'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { EXTERNAL_COUNTRIES_GROUP_ID } from '../../constants'
import { useConfig } from '../../providers/config'
import { useApi } from '../useApi'

export const useWorldStores = ({ options } = {}) => {
	const { formatMessage } = useIntl()
	const { data, error, isValidating, mutate, cancel, cancelled } = useApi({
		method: getCoreGeolocationGeocountries,
		options,
	})

	const {
		availableStores,
		geoip: { countryUrls }, // external countries
	} = useConfig()

	const externalCountries = useMemo(
		() =>
			countryUrls?.map(({ countryCode, url }) => ({
				name: formatMessage({ id: `country_${countryCode}` }),
				code: countryCode,
				url,
			})) || [],
		[countryUrls, formatMessage]
	)

	const countries = useMemo(() => {
		return (
			[
				...(data?.reduce(
					(acc, { countries }) =>
						acc.concat(
							countries.map((country) => ({ ...country, name: formatMessage({ id: `country_${country.code}` }) }))
						),
					[]
				) || []),
				...externalCountries,
			].sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)) ?? []
		)
	}, [data, externalCountries, formatMessage])

	const countriesMap = useMemo(() => {
		if (data) {
			const result = new Map()
			data.forEach(({ countries }) => {
				countries.forEach((country) => {
					result.set(country.code, { ...country, name: formatMessage({ id: `country_${country.code}` }) })
				})
			})
			countryUrls?.forEach(({ countryCode, url }) => {
				result.set(countryCode, { code: countryCode, name: formatMessage({ id: `country_${countryCode}` }), url })
			})
			return result
		} else {
			return new Map()
		}
	}, [countryUrls, data, formatMessage])

	const storesByGroupId = useMemo(() => groupBy(availableStores, 'groupId') || [], [availableStores])

	const continents = useMemo(() => {
		return [
			...(data || []),
			...(externalCountries.length > 0 ? [{ groupId: EXTERNAL_COUNTRIES_GROUP_ID, countries: externalCountries }] : []),
		]
	}, [data, externalCountries])

	return {
		continents,
		countries,
		countriesMap,
		isSingleWebsite: Object.keys(storesByGroupId).length === 1,
		loading: isValidating,
		error,
		mutate,
		cancel,
		cancelled,
	}
}
